// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import HeaderClickable, { HeaderClickableStyle } from "@ces/App/Components/Header/HeaderClickable";
import MiniCart from "@ces/App/Components/MiniCart/MiniCart";
import { TippyPopupStyle } from "@ces/App/Components/Tippy/Style";
import TippyPopup from "@ces/App/Components/Tippy/TippyPopup";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { xHasRole } from "@ces/App/Store/Selectors/GeneralSelectors";
import { USER_ROLES } from "@ces/XDL/constants";
import ArrowDown from "@ces/XDL/Icons/ArrowDown";
import Cart from "@ces/XDL/Icons/Cart";
import CartFilled from "@ces/XDL/Icons/CartFilled";
import { parseBoolean } from "@ces/XDL/Utilities";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
    isAccounting: xHasRole(state, USER_ROLES.Accounting),
    isCartPage: getCurrentPage(state).type === "CartPage",
    currentBillTo: getBillToState(state, state.context.session.billToId).value,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // XNG-Change
    tippyPopup?: TippyPopupStyle;
    cartLink?: HeaderClickableStyle;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: Cart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    // XNG-Changes
    isTabletScreen,
    isAccounting,
    isCartPage,
    currentBillTo,
}) => {
    const [isPopupShown, setIsPopupShown] = React.useState(false);
    const history = useHistory();

    useEffect(() => {
        // XNG-Change
        if (shouldLoadCart && !isCartPage) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart, isCartPage]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    const openCartPageHandler = () => {
        if (!cartUrl) {
            return;
        }
        history.push(cartUrl);
    };

    const headerClickable = (
        <HeaderClickable
            onClick={openCartPageHandler}
            iconProps={{
                src: isPopupShown ? (selectedIcon === "cart" ? CartFilled : icon) : icon,
            }}
            data-test-selector="cartLink"
            title={translate("Cart")}
            cartLines={retainedTotalCountToDisplay}
            isOpen={isPopupShown}
            rightIconProps={
                !isTabletScreen
                    ? {
                          src: ArrowDown,
                      }
                    : undefined
            }
            extendedStyles={styles.cartLink}
        />
    );

    // XNG-changes
    if (isAccounting || parseBoolean(currentBillTo?.properties?.viewOnly)) {
        return null;
    }
    if (isTabletScreen) {
        return headerClickable;
    }

    return (
        <TippyPopup
            extendedStyles={styles.tippyPopup}
            maxWidth={466}
            content={<MiniCart isPopupShown={isPopupShown} />}
            arrow={true}
            offset={[0, 18]}
            placement="bottom-end"
            type="menu"
            onTriggerPopupOpen={isOpen => setIsPopupShown(isOpen)}
            popperOptions={{
                strategy: "absolute",
                modifiers: [
                    {
                        name: "preventOverflow",
                        options: {
                            padding: 0,
                        },
                    },
                    {
                        name: "computeStyles",
                        options: {
                            gpuAcceleration: false,
                            roundOffsets: ({ x, y }: any) => ({
                                x,
                                y: y - 6,
                            }),
                        },
                    },
                ],
            }}
        >
            {headerClickable}
        </TippyPopup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
